import { useCallback, useState, useMemo, RefObject } from "react";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

export const IMAGE_BASE_WIDTH = 2000;

export type MapState = {
  /** 現在のスケール（反転した値） */
  scale: number;
  /** IMAGE_BASE_WIDTH (2000px) に対する表示上の比率 */
  mapRatio: number;
  /** 画像の基準幅 (2000px) */
  imageWidth: number;
  /** 画像の基準高さ（アスペクト比を維持） */
  imageHeight: number;
  /** 現在の表示範囲（2000px基準の座標系） */
  viewport?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
};

export const useMapState = (imageRef: RefObject<HTMLImageElement>, transformRef?: RefObject<ReactZoomPanPinchRef>) => {
  const [scale, setScale] = useState(1);
  const [viewport, setViewport] = useState<{ top: number; left: number; width: number; height: number }>();
  const [, setTick] = useState(0);

  const calculateViewport = useCallback(
    (transformState: { positionX: number; positionY: number; scale: number }) => {
      if (!imageRef.current) return;

      const { positionX, positionY, scale: s } = transformState;
      const naturalWidth = imageRef.current.naturalWidth;
      const offsetWidth = imageRef.current.offsetWidth;

      if (naturalWidth > 0 && offsetWidth > 0) {
        const ratio = IMAGE_BASE_WIDTH / (offsetWidth * s);
        const wrapperElement = transformRef?.current?.instance.wrapperComponent;
        const viewportWidth = wrapperElement?.offsetWidth ?? imageRef.current.offsetWidth;
        const viewportHeight = wrapperElement?.offsetHeight ?? imageRef.current.offsetHeight;

        setViewport({
          top: -positionY * ratio,
          left: -positionX * ratio,
          width: viewportWidth * ratio,
          height: viewportHeight * ratio,
        });
      }
    },
    [imageRef, transformRef]
  );

  const updateMapState = useCallback(
    (currentScale?: number, transformState?: { positionX: number; positionY: number; scale: number }) => {
      setTick((t) => t + 1);
      if (currentScale !== undefined) {
        setScale(1 / currentScale);
      }

      if (transformState) {
        calculateViewport(transformState);
      }
    },
    [calculateViewport]
  );

  const mapState: MapState = useMemo(() => {
    const offsetWidth = imageRef.current?.offsetWidth ?? 0;
    const offsetHeight = imageRef.current?.offsetHeight ?? 0;
    const naturalWidth = imageRef.current?.naturalWidth ?? 0;
    const naturalHeight = imageRef.current?.naturalHeight ?? 0;

    return {
      scale,
      mapRatio: offsetWidth / IMAGE_BASE_WIDTH,
      imageWidth: IMAGE_BASE_WIDTH,
      imageHeight: naturalWidth > 0 ? Math.round((IMAGE_BASE_WIDTH * naturalHeight) / naturalWidth) : offsetHeight,
      viewport,
    };
  }, [
    scale,
    viewport,
    imageRef.current?.offsetWidth,
    imageRef.current?.offsetHeight,
    imageRef.current?.naturalWidth,
    imageRef.current?.naturalHeight,
  ]);

  return {
    mapState,
    updateMapState,
  };
};
